import { Link } from "react-router-dom"
import { classNames } from "utils/ClassNames"
import { UniqueGradient } from "./UniqueGradient"
import dayjs from "dayjs"
import { LockClosedIcon, LockOpenIcon, UsersIcon } from "@heroicons/react/24/outline"
import { formatCompactTimeDiff } from "utils/FormatTimeDiff"

type Props = {
  id: string
  name: string
  /** Path appended to `/time-capsules/` (e.g. a slug, or `shared/{creatorId}/{slug}`). */
  routePath: string
  opensAt?: string
  isSelected: boolean
  /** Name of the creator, shown when the capsule is not owned by the current user. */
  ownerName?: string
  /** Owned capsule that has at least one other member. */
  hasCollaborators?: boolean
  onClick?: () => void
}

export const TimeCapsuleListItem = ({
  id,
  name,
  routePath,
  opensAt,
  isSelected,
  ownerName,
  hasCollaborators,
  onClick
}: Props) => {
  const isLocked = !!opensAt && dayjs(opensAt).isAfter(dayjs())

  const timing = !opensAt
    ? "Draft"
    : isLocked
      ? `Opens in ${formatCompactTimeDiff(opensAt)}`
      : `Opened ${formatCompactTimeDiff(opensAt)} ago`

  return (
    <li>
      <Link
        to={`/time-capsules/${routePath}`}
        onClick={onClick}
        className={classNames(
          "group flex items-center gap-x-3 rounded-md p-2 hover:bg-gray-50 dark:hover:bg-gray-800",
          isSelected ? "bg-gray-50 dark:bg-gray-800" : ""
        )}
      >
        <UniqueGradient
          id={id}
          className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg"
        >
          {isLocked ? (
            <LockClosedIcon aria-hidden="true" className="h-4 w-4 text-white" />
          ) : (
            <LockOpenIcon aria-hidden="true" className="h-4 w-4 text-white" />
          )}
        </UniqueGradient>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-x-1.5">
            <span
              className={classNames(
                "truncate text-sm font-semibold",
                isSelected
                  ? "text-primary-600 dark:text-primary-400"
                  : "text-gray-900 dark:text-gray-100 group-hover:text-primary-600 dark:group-hover:text-primary-400"
              )}
            >
              {name}
            </span>
            {hasCollaborators && (
              <UsersIcon
                aria-label="Shared with collaborators"
                className="h-3.5 w-3.5 shrink-0 text-gray-400 dark:text-gray-500"
              />
            )}
          </div>
          <span className="block truncate text-xs text-gray-500 dark:text-gray-400">
            {ownerName ? `by ${ownerName} · ${timing}` : timing}
          </span>
        </div>
      </Link>
    </li>
  )
}
